import { boot, ensureStage, prepareBackendRecovery, type Loaded } from "./assets";
import { createBootReporter } from "./boot-progress";
import {
  applyControls,
  describeTrack,
  closeCamera,
  openCamera,
  readSettings,
  watchCamera,
  type Camera,
  type CameraSurface,
} from "./camera";
import { createSession, INTERACTION_SETTLE_MS, type Session } from "./session";
import type { Framing, ScannerEndpoint, StopReason } from "../protocol/events";
import { clock, log, warn } from "../shared/debug";
import { onSettingChange, settings } from "../shared/settings";

/**
 * The scanner: the recognition half of the app, behind its endpoint.
 *
 * Owns the loaded models, the camera and the running session, and holds the latest word
 * from the view — where to look, whether the user is holding the screen — for the loop
 * to read. Everything it does is either a reaction to a view event or a boot; everything
 * it learns leaves as a scanner event. There is no other way in or out.
 */
export interface Scanner {
  /**
   * Fetch the models and the index. Resolves either way — the outcome is reported as
   * `boot:ready` or `boot:error`. The camera preview can precede readiness; recognition
   * starts only once required assets and the requested camera are both available.
   */
  boot(): Promise<void>;
}

export function createScanner(endpoint: ScannerEndpoint, surface: CameraSurface): Scanner {
  let loaded: Loaded | null = null;
  let session: Session | null = null;
  /** Set while `getUserMedia` is pending, so permission taps cannot open twice. */
  let starting = false;
  let scanRequested = false;
  let cameraGeneration = 0;
  let booting: Promise<void> | null = null;

  // The view's latest word, read by the loop per iteration. Cleared with the session, so
  // a framing computed against one camera's raster can never aim the next one.
  let framing: Framing | null = null;
  let interacting = false;
  let settleUntil = 0;

  endpoint.on("framing", (next) => {
    framing = next;
  });
  endpoint.on("interaction", ({ active }) => {
    interacting = active;
    if (!active) settleUntil = performance.now() + INTERACTION_SETTLE_MS;
  });
  endpoint.on("activity", () => {
    settleUntil = performance.now() + INTERACTION_SETTLE_MS;
  });

  const inputs = {
    framing: () => framing,
    interacting: () => interacting || performance.now() < settleUntil,
  };

  /** The open camera, for the track controls; null between sessions. */
  let camera: Camera | null = null;

  /** Stops hearing the open camera's track end; set with the camera, cleared with it. */
  let unwatchCamera: (() => void) | null = null;

  const stop = (reason: StopReason): void => {
    scanRequested = false;
    cameraGeneration++;
    const ending = session;
    const opened = camera;
    session = null;
    camera = null;
    framing = null;
    unwatchCamera?.();
    unwatchCamera = null;
    if (ending) ending.stop();
    else if (opened) closeCamera(surface, opened);
    endpoint.emit("session:stopped", { reason });
  };

  // The camera track's own controls — torch, focus, exposure, native zoom — asked for by
  // the view and applied here, because only this side holds the stream. Requests are
  // queued so two sliders moving at once cannot interleave their constraint calls.
  const track = (): MediaStreamTrack | null => camera?.stream.getVideoTracks()[0] ?? null;
  const describe = (): void => {
    const current = track();
    if (!camera || !current) return;
    const { label, capabilities, settings } = describeTrack(current);
    endpoint.emit("camera:capabilities", {
      label,
      capabilities,
      settings,
      initial: camera.initialSettings,
    });
  };
  let controlQueue: Promise<void> = Promise.resolve();
  endpoint.on("camera:query", describe);
  endpoint.on("camera:control", (request) => {
    // A queued control belongs to the camera that received it, even if another camera
    // opens before the preceding request finishes.
    const current = track();
    const open = camera;
    const generation = cameraGeneration;
    const changed = (): boolean => camera !== open || cameraGeneration !== generation;
    controlQueue = controlQueue.then(async () => {
      const unavailable = (message: string, elapsed = 0): void => {
        endpoint.emit("camera:applied", {
          request,
          ok: false,
          message,
          ms: elapsed,
          settings: {},
        });
      };
      if (!open || !current) {
        unavailable("no camera open");
        return;
      }
      if (changed()) {
        unavailable("camera changed");
        return;
      }
      const took = clock();
      try {
        const result = await applyControls(current, request, open.initialSettings);
        if (changed()) {
          unavailable("camera changed", took());
          return;
        }
        log(
          "detect",
          `camera control ${JSON.stringify(request)} → ${result.ok ? "applied" : "not applied"}` +
            (result.message ? ` (${result.message})` : ""),
        );
        endpoint.emit("camera:applied", { request, ...result, ms: took() });
      } catch (error) {
        if (changed()) {
          unavailable("camera changed", took());
          return;
        }
        warn("detect", `camera control ${JSON.stringify(request)} failed`, error);
        endpoint.emit("camera:applied", {
          request,
          ok: false,
          message: (error as Error).message,
          ms: took(),
          settings: readSettings(current),
        });
      }
    });
  });

  const beginSession = (): void => {
    if (!scanRequested || !loaded || !camera || session) return;
    const opened = camera;
    try {
      const next = createSession(loaded, surface, opened, endpoint, inputs);
      session = next;
      // Announced before the first frame is pulled: the view answers with a framing in
      // its handler, and the loop's first iteration finds it already there. A `stop`
      // that arrives inside that handler — a tab that went hidden while the camera was
      // opening — finds `session` set and ends it cleanly before it ever runs.
      endpoint.emit("session:started", {
        sensorWidth: opened.sensorWidth,
        sensorHeight: opened.sensorHeight,
      });
      if (session === next) {
        // What the track can do, once the view knows the session exists to ask about.
        describe();
        next.run();
      }
    } catch (error) {
      closeCamera(surface, opened);
      camera = null;
      scanRequested = false;
      warn("detect", "could not start recognition", error);
      endpoint.emit("camera:failed", { message: (error as Error).message });
    }
  };

  const prepareCamera = async (): Promise<void> => {
    scanRequested = true;
    if (camera) {
      beginSession();
      return;
    }
    if (starting) return;
    starting = true;
    const generation = cameraGeneration;
    try {
      const opened = await openCamera(surface);
      if (generation !== cameraGeneration) {
        closeCamera(surface, opened);
        return;
      }
      if (!scanRequested || document.hidden) {
        closeCamera(surface, opened);
        const reason: StopReason = scanRequested ? "hidden" : "requested";
        scanRequested = false;
        endpoint.emit("session:stopped", { reason });
        return;
      }
      camera = opened;
      // The device can take the camera back at any moment — a call, another app, the
      // browser's own camera control. The loop cannot see that: the surface just keeps
      // its last frame. Only the track says, and only the camera it was set on counts.
      unwatchCamera = watchCamera(opened, () => {
        if (camera !== opened) return;
        warn("detect", "camera track ended under the session — the device took the camera");
        stop("camera-lost");
      });
      endpoint.emit("camera:opened", {
        sensorWidth: opened.sensorWidth,
        sensorHeight: opened.sensorHeight,
      });
      beginSession();
    } catch (error) {
      if (generation === cameraGeneration) {
        scanRequested = false;
        warn("detect", "getUserMedia failed", error);
        endpoint.emit("camera:failed", { message: (error as Error).message });
      }
    } finally {
      starting = false;
      if (generation !== cameraGeneration && scanRequested && !document.hidden) {
        void prepareCamera();
      }
    }
  };

  endpoint.on("start", () => void prepareCamera());
  endpoint.on("camera:prepare", () => void prepareCamera());
  endpoint.on("stop", () => stop("requested"));

  // A pending permission request and a prepared preview have the same camera lifetime
  // as a running session, even before the view can describe them as scanning.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden && (camera || starting)) stop("hidden");
  });

  // A stage switched on in the settings may need the asset boot deliberately skipped.
  // The panel writes the store and nothing else; this is where the store is watched.
  onSettingChange((key) => {
    if (!loaded) return;
    if ((key === "orb" || key === "ocr" || key === "setSymbols") && settings[key]) {
      void ensureStage(loaded, key);
    }
  });

  const reporter = createBootReporter(endpoint);
  const runBoot = (): Promise<void> => {
    if (loaded) return Promise.resolve();
    if (booting) return booting;
    booting = (async () => {
      try {
        const result = await boot(reporter);
        loaded = result;
        endpoint.emit("boot:ready", { backend: result.backend, warnings: result.warnings });
        beginSession();
      } catch (error) {
        warn("boot", "startup failed", error);
        if (prepareBackendRecovery(error)) {
          // ORT caches failed initialization and may retain partial GPU allocations.
          // One fresh page is required; the tab marker prevents a reload loop.
          stop("requested");
          log("boot", "restarting once with the CPU runtime after GPU initialization failed");
          location.reload();
          return;
        }
        endpoint.emit("boot:error", { message: (error as Error).message });
      }
    })().finally(() => {
      booting = null;
    });
    return booting;
  };
  endpoint.on("boot:retry", () => {
    if (loaded) return;
    const previous = booting;
    // In-flight successful transfers are allowed to land; a retry never creates two
    // model builds or resets a transfer that is still making progress.
    void (previous ?? Promise.resolve()).then(() => {
      if (loaded || booting) return;
      reporter.resetFailures?.();
      endpoint.emit("boot:retrying");
      void runBoot();
    });
  });
  return { boot: runBoot };
}
